/**
 * Admin Sales Overview - Team performance dashboard
 *
 * Admin view showing all salespeople's performance metrics,
 * conversion rates, and commission totals.
 */
import { useState, useEffect } from 'react'
import {
  TrendingUp,
  Users,
  Target,
  PoundSterling,
  RefreshCw,
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
} from 'lucide-react'
import { getAdminSalesOverview } from '@/services/salesApi'
import type { AdminSalesOverview, SalespersonPerformance } from '@/types'

export default function SalesOverview() {
  const [overview, setOverview] = useState<AdminSalesOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Current month/year for filtering
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  useEffect(() => {
    fetchOverview()
  }, [selectedMonth, selectedYear])

  const fetchOverview = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getAdminSalesOverview(selectedMonth, selectedYear)
      setOverview(data)
    } catch {
      setError('Failed to load sales overview')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1, 1).toLocaleString('en-GB', { month: 'long' })
  }

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12)
      setSelectedYear(selectedYear - 1)
    } else {
      setSelectedMonth(selectedMonth - 1)
    }
  }

  const goToNextMonth = () => {
    const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()
    if (isCurrentMonth) return

    if (selectedMonth === 12) {
      setSelectedMonth(1)
      setSelectedYear(selectedYear + 1)
    } else {
      setSelectedMonth(selectedMonth + 1)
    }
  }

  const isCurrentMonth = selectedMonth === now.getMonth() + 1 && selectedYear === now.getFullYear()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 text-slate-500 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <p className="text-slate-300 mb-2">{error}</p>
        <button onClick={fetchOverview} className="btn-secondary text-sm">
          Try Again
        </button>
      </div>
    )
  }

  // Calculate team totals
  const teamTotals = overview?.salespeople?.reduce(
    (acc, sp) => ({
      activeProspects: acc.activeProspects + sp.activeProspects,
      prospectsWon: acc.prospectsWon + sp.prospectsWon,
      prospectsLost: acc.prospectsLost + sp.prospectsLost,
      totalCommissionThisMonth: acc.totalCommissionThisMonth + sp.totalCommissionThisMonth,
      totalCommissionAllTime: acc.totalCommissionAllTime + sp.totalCommissionAllTime,
      activeClients: acc.activeClients + sp.activeClients,
    }),
    {
      activeProspects: 0,
      prospectsWon: 0,
      prospectsLost: 0,
      totalCommissionThisMonth: 0,
      totalCommissionAllTime: 0,
      activeClients: 0,
    }
  ) || {
    activeProspects: 0,
    prospectsWon: 0,
    prospectsLost: 0,
    totalCommissionThisMonth: 0,
    totalCommissionAllTime: 0,
    activeClients: 0,
  }

  const teamConversionRate =
    teamTotals.prospectsWon + teamTotals.prospectsLost > 0
      ? (teamTotals.prospectsWon / (teamTotals.prospectsWon + teamTotals.prospectsLost)) * 100
      : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10">
            <TrendingUp className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Sales Overview</h1>
            <p className="text-slate-400">Team performance and metrics</p>
          </div>
        </div>
        <button onClick={fetchOverview} className="btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Month Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-lg font-semibold text-white">
              {getMonthName(selectedMonth)} {selectedYear}
            </span>
          </div>
          <button
            onClick={goToNextMonth}
            disabled={isCurrentMonth}
            className={`p-2 rounded-lg transition-colors ${
              isCurrentMonth ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-700'
            }`}
          >
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Team Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Active Prospects</span>
          </div>
          <p className="text-2xl font-bold text-white">{overview?.totalActiveProspects || 0}</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-2">
            <ArrowUpRight className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Won This Month</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{overview?.totalWonThisMonth || 0}</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-slate-400">Conversion Rate</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">{teamConversionRate.toFixed(1)}%</p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-2">
            <PoundSterling className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-400">Commission (Month)</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">
            {formatCurrency(overview?.totalCommissionThisMonth || 0)}
          </p>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-slate-400">Active Clients</span>
          </div>
          <p className="text-2xl font-bold text-cyan-400">{teamTotals.activeClients}</p>
        </div>
      </div>

      {/* Salespeople Performance Table */}
      <div className="card">
        <div className="p-4 border-b border-slate-700/50">
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Team Performance
          </h2>
        </div>

        {!overview?.salespeople?.length ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-200 mb-2">No salespeople data</h3>
            <p className="text-slate-400">
              No sales activity for {getMonthName(selectedMonth)} {selectedYear}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-xs text-slate-500 uppercase bg-slate-800/30">
                <tr>
                  <th className="p-4 text-left">Salesperson</th>
                  <th className="p-4 text-center">Active Prospects</th>
                  <th className="p-4 text-center">Won</th>
                  <th className="p-4 text-center">Lost</th>
                  <th className="p-4 text-center">Conversion</th>
                  <th className="p-4 text-right">Commission (Month)</th>
                  <th className="p-4 text-right">Commission (All Time)</th>
                  <th className="p-4 text-center">Active Clients</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {overview.salespeople.map((sp) => (
                  <SalespersonRow key={sp.userId} salesperson={sp} />
                ))}
              </tbody>
              {/* Totals Row */}
              <tfoot className="bg-slate-800/50 font-semibold">
                <tr>
                  <td className="p-4 text-slate-200">Team Total</td>
                  <td className="p-4 text-center text-slate-200">{teamTotals.activeProspects}</td>
                  <td className="p-4 text-center text-emerald-400">{teamTotals.prospectsWon}</td>
                  <td className="p-4 text-center text-red-400">{teamTotals.prospectsLost}</td>
                  <td className="p-4 text-center text-purple-400">{teamConversionRate.toFixed(1)}%</td>
                  <td className="p-4 text-right text-amber-400">
                    {formatCurrency(teamTotals.totalCommissionThisMonth)}
                  </td>
                  <td className="p-4 text-right text-emerald-400">
                    {formatCurrency(teamTotals.totalCommissionAllTime)}
                  </td>
                  <td className="p-4 text-center text-cyan-400">{teamTotals.activeClients}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Performance Insights */}
      {overview?.salespeople && overview.salespeople.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performers */}
          <div className="card">
            <div className="p-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                Top Performers
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {[...overview.salespeople]
                .sort((a, b) => b.totalCommissionThisMonth - a.totalCommissionThisMonth)
                .slice(0, 3)
                .map((sp, idx) => (
                  <div key={sp.userId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          idx === 0
                            ? 'bg-amber-500/20 text-amber-400'
                            : idx === 1
                            ? 'bg-slate-400/20 text-slate-300'
                            : 'bg-amber-700/20 text-amber-600'
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <span className="text-slate-200">{sp.userName}</span>
                    </div>
                    <span className="text-emerald-400 font-medium">
                      {formatCurrency(sp.totalCommissionThisMonth)}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Conversion Leaders */}
          <div className="card">
            <div className="p-4 border-b border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Conversion Leaders
              </h3>
            </div>
            <div className="p-4 space-y-3">
              {[...overview.salespeople]
                .filter((sp) => sp.prospectsWon + sp.prospectsLost >= 3) // At least 3 outcomes
                .sort((a, b) => b.conversionRate - a.conversionRate)
                .slice(0, 3)
                .map((sp, idx) => (
                  <div key={sp.userId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          idx === 0
                            ? 'bg-purple-500/20 text-purple-400'
                            : idx === 1
                            ? 'bg-slate-400/20 text-slate-300'
                            : 'bg-purple-700/20 text-purple-600'
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <span className="text-slate-200">{sp.userName}</span>
                    </div>
                    <span className="text-purple-400 font-medium">{sp.conversionRate.toFixed(1)}%</span>
                  </div>
                ))}
              {overview.salespeople.filter((sp) => sp.prospectsWon + sp.prospectsLost >= 3).length ===
                0 && (
                <p className="text-slate-500 text-sm text-center py-4">
                  Not enough data yet (need 3+ outcomes)
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SalespersonRow({ salesperson }: { salesperson: SalespersonPerformance }) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <tr className="hover:bg-slate-800/30 transition-colors">
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <span className="text-blue-400 font-medium text-sm">
              {salesperson.userName?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <span className="text-slate-200 font-medium">{salesperson.userName || 'Unknown'}</span>
        </div>
      </td>
      <td className="p-4 text-center text-slate-300">{salesperson.activeProspects}</td>
      <td className="p-4 text-center">
        <span className="inline-flex items-center gap-1 text-emerald-400">
          <ArrowUpRight className="w-3 h-3" />
          {salesperson.prospectsWon}
        </span>
      </td>
      <td className="p-4 text-center">
        <span className="inline-flex items-center gap-1 text-red-400">
          <ArrowDownRight className="w-3 h-3" />
          {salesperson.prospectsLost}
        </span>
      </td>
      <td className="p-4 text-center">
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            salesperson.conversionRate >= 50
              ? 'bg-emerald-500/10 text-emerald-400'
              : salesperson.conversionRate >= 25
              ? 'bg-amber-500/10 text-amber-400'
              : 'bg-red-500/10 text-red-400'
          }`}
        >
          {salesperson.conversionRate.toFixed(1)}%
        </span>
      </td>
      <td className="p-4 text-right text-amber-400 font-medium">
        {formatCurrency(salesperson.totalCommissionThisMonth)}
      </td>
      <td className="p-4 text-right text-emerald-400 font-medium">
        {formatCurrency(salesperson.totalCommissionAllTime)}
      </td>
      <td className="p-4 text-center text-cyan-400">{salesperson.activeClients}</td>
    </tr>
  )
}
